import { createContext, useContext, useState, ReactNode } from "react";

type Language = "el" | "en";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<Language, Record<string, string>> = {
  el: {
    // Header
    "nav.home": "Αρχική",
    "nav.about": "Η Εταιρεία Μας",
    "nav.services": "Υπηρεσίες",
    "nav.contact": "Επικοινωνία",
    "nav.call": "Καλέστε Μας",
    
    // Services submenu
    "services.installation": "Μελέτη & Εγκατάσταση",
    "services.maintenance": "Συντήρηση & Επισκευή",
    "services.modernization": "Εκσυγχρονισμός",
    "services.accessibility": "Συστήματα ΑΜΕΑ",
    "services.freight": "Ανελκυστήρες Φορτίων",
    "services.escalators": "Κυλιόμενες Κλίμακες",
    
    // Hero Section
    "hero.badge": "Από το 1935 στη Θεσσαλονίκη",
    "hero.title1": "Ανελκυστήρες Υψηλής Ποιότητας",
    "hero.title2": "Εγκατάσταση & Συντήρηση",
    "hero.title3": "24ωρη Εξυπηρέτηση",
    "hero.title4": "Από το 1935",
    "hero.description": "Η LIFTCO είναι μία από τις πρωτοπόρες εταιρείες στον τομέα των ανελκυστήρων στη Θεσσαλονίκη και τη Βόρεια Ελλάδα. Εγκατάσταση, συντήρηση και εκσυγχρονισμός με συνέπεια και επαγγελματισμό.",
    "hero.cta": "Καλέστε Μας Τώρα",
    "hero.services": "Οι Υπηρεσίες Μας",
    
    // Features
    "features.title": "Γιατί να Επιλέξετε τη LIFTCO",
    "features.subtitle": "Με πολυετή εμπειρία και δέσμευση στην ποιότητα, προσφέρουμε αξιόπιστες λύσεις για κάθε ανάγκη μετακίνησης.",
    "features.since": "Από το 1935",
    "features.since.desc": "Σχεδόν 90 χρόνια εμπειρίας στον χώρο των ανελκυστήρων.",
    "features.quality": "Πιστοποιημένη Ποιότητα",
    "features.quality.desc": "Προϊόντα πιστοποιημένα κατά DIN ISO 9001/1992.",
    "features.staff": "Έμπειρο Προσωπικό",
    "features.staff.desc": "10+ εξειδικευμένοι τεχνικοί στην υπηρεσία σας.",
    "features.service": "24ωρη Εξυπηρέτηση",
    "features.service.desc": "Άμεση ανταπόκριση σε κάθε ανάγκη, οποιαδήποτε ώρα.",
    
    // Services Section
    "services.title": "Οι Υπηρεσίες Μας",
    "services.subtitle": "Καλύπτουμε κάθε ανάγκη στον τομέα των ανελκυστήρων και κυλιόμενων κλιμάκων με επαγγελματισμό και αξιοπιστία.",
    "services.installation.desc": "Πλήρης σχεδιασμός και εγκατάσταση ανελκυστήρων υψηλής ποιότητας για κάθε κτίριο.",
    "services.maintenance.desc": "Τακτική συντήρηση και άμεση επισκευή για ασφαλή λειτουργία του ανελκυστήρα σας.",
    "services.modernization.desc": "Αναβάθμιση παλαιών ανελκυστήρων με σύγχρονη τεχνολογία και αισθητική.",
    "services.accessibility.desc": "Λύσεις προσβασιμότητας για άτομα με κινητικά προβλήματα.",
    "services.freight.desc": "Βιομηχανικοί ανελκυστήρες για αποθήκες και εμπορικά κέντρα.",
    
    // About Section
    "about.title": "Η Εταιρεία",
    "about.description1": "Η τεχνική εταιρεία ανελκυστήρων LIFTCO Ε.Ε. η οποία βρίσκεται στην Θεσσαλονίκη, δραστηριοποιείται στο χώρο των ανελκυστήρων από το 1935 με πολύ υψηλά επίπεδα παραγωγικότητας σε τρεις κύριους τομείς: εγκατάσταση, επισκευή και συντήρηση ανελκυστήρων, κυλιόμενων κλιμάκων και πάσης φύσεως ανυψωτικού προσώπων και εμπορευμάτων.",
    "about.description2": "Με μεγάλο κύκλο εργασιών και 10 εργαζομένους, κατέχει μία από τις καλύτερες θέσεις στον τομέα των ανελκυστήρων και κυλιόμενων κλιμάκων στην Θεσσαλονίκη και στην Βόρεια Ελλάδα.",
    "about.cert": "Πιστοποίηση DIN ISO 9001/1992",
    "about.staff": "Εξειδικευμένο ανθρώπινο δυναμικό",
    "about.equipment": "Σύγχρονος εξοπλισμός",
    "about.prices": "Οικονομικές τιμές",
    "about.more": "Μάθετε Περισσότερα",
    
    // CTA Section
    "cta.title": "Χρειάζεστε Ανελκυστήρα;",
    "cta.description": "Επικοινωνήστε μαζί μας για να συζητήσουμε τις ανάγκες σας και να βρούμε μαζί τις ιδανικές λύσεις. Εξυπηρετούμε άμεσα τη Θεσσαλονίκη και όλη τη Βόρεια Ελλάδα.",
    
    // Stats
    "stats.title": "Η LIFTCO σε",
    "stats.titleHighlight": "Αριθμούς",
    "stats.subtitle": "Δεκαετίες εμπειρίας και χιλιάδες επιτυχημένα έργα μας καθιστούν τον αξιόπιστο συνεργάτη σας.",
    "stats.years": "Χρόνια Εμπειρίας",
    "stats.installations": "Εγκαταστάσεις",
    "stats.clients": "Ικανοποιημένοι Πελάτες",
    "stats.support": "Υποστήριξη",
    
    // Testimonials
    "testimonials.title": "Τι Λένε οι Πελάτες Μας",
    "testimonials.subtitle": "Η εμπιστοσύνη χιλιάδων πελατών μας δίνει δύναμη να συνεχίζουμε με το ίδιο πάθος.",
    
    // Google Reviews
    "reviews.title": "Αξιολογήσεις",
    "reviews.count": "αξιολογήσεις στο Google",
    "reviews.reviewFrom": "Αξιολόγηση από",
    "reviews.seeAll": "Δείτε όλες τις αξιολογήσεις στο Google Maps",
    
    // Footer
    "footer.description": "Τεχνική Εταιρεία Ανελκυστήρων & Κυλιόμενων Κλιμάκων στη Θεσσαλονίκη από το 1935.",
    "footer.quicklinks": "Σύνδεσμοι",
    "footer.services": "Υπηρεσίες",
    "footer.contact": "Επικοινωνία",
    "footer.rights": "Με επιφύλαξη παντός δικαιώματος.",
    "footer.serving": "Εξυπηρετούμε Θεσσαλονίκη & Βόρεια Ελλάδα",
    "footer.24h": "24ωρη Εξυπηρέτηση",

    // Common
    "common.more": "Περισσότερα",
    "common.allServices": "Όλες οι Υπηρεσίες",
    "common.contactForm": "Επικοινωνία",
    "common.needHelp": "Χρειάζεστε Βοήθεια;",
    "common.callNow": "Καλέστε Μας Τώρα",

    // Company
    "company.address": "Αμύντα 18, Θεσσαλονίκη",

    // Not Found
    "notfound.title": "Η σελίδα δεν βρέθηκε",
    "notfound.subtitle": "Η σελίδα που ψάχνετε δεν υπάρχει.",
    "notfound.home": "Αρχική Σελίδα",

    // Service Detail
    "serviceDetail.includes": "Τι Περιλαμβάνει",
    "serviceDetail.requestQuote": "Ζητήστε Προσφορά",
    "serviceDetail.requestQuoteText": "Επικοινωνήστε μαζί μας για δωρεάν εκτίμηση και προσφορά χωρίς δέσμευση.",

    // Loading
    "loading": "Φόρτωση",

    // Cookie Consent
    "cookie.title": "🍪 Χρήση Cookies",
    "cookie.message": "Χρησιμοποιούμε cookies για τη βελτίωση της εμπειρίας σας στον ιστότοπό μας. Συνεχίζοντας την περιήγησή σας, συμφωνείτε με τη χρήση τους.",
    "cookie.accept": "Αποδοχή",
    "cookie.reject": "Απόρριψη",
  },
  en: {
    // Header
    "nav.home": "Home",
    "nav.about": "About Us",
    "nav.services": "Services",
    "nav.contact": "Contact",
    "nav.call": "Call Us",
    
    // Services submenu
    "services.installation": "Design & Installation",
    "services.maintenance": "Maintenance & Repair",
    "services.modernization": "Modernization",
    "services.accessibility": "Accessibility Systems",
    "services.freight": "Freight Elevators",
    "services.escalators": "Escalators",
    
    // Hero Section
    "hero.badge": "Since 1935 in Thessaloniki",
    "hero.title1": "High Quality Elevators",
    "hero.title2": "Installation & Maintenance",
    "hero.title3": "24/7 Service",
    "hero.title4": "Since 1935",
    "hero.description": "LIFTCO is one of the pioneering companies in the elevator sector in Thessaloniki and Northern Greece. Installation, maintenance and modernization with consistency and professionalism.",
    "hero.cta": "Call Us Now",
    "hero.services": "Our Services",
    
    // Features
    "features.title": "Why Choose LIFTCO",
    "features.subtitle": "With years of experience and commitment to quality, we offer reliable solutions for every mobility need.",
    "features.since": "Since 1935",
    "features.since.desc": "Almost 90 years of experience in the elevator industry.",
    "features.quality": "Certified Quality",
    "features.quality.desc": "Products certified according to DIN ISO 9001/1992.",
    "features.staff": "Experienced Staff",
    "features.staff.desc": "10+ specialized technicians at your service.",
    "features.service": "24/7 Service",
    "features.service.desc": "Immediate response to every need, any time.",
    
    // Services Section
    "services.title": "Our Services",
    "services.subtitle": "We cover every need in elevators and escalators with professionalism and reliability.",
    "services.installation.desc": "Complete design and installation of high-quality elevators for every building.",
    "services.maintenance.desc": "Regular maintenance and immediate repair for safe operation of your elevator.",
    "services.modernization.desc": "Upgrading old elevators with modern technology and aesthetics.",
    "services.accessibility.desc": "Accessibility solutions for people with mobility issues.",
    "services.freight.desc": "Industrial elevators for warehouses and commercial centers.",
    
    // About Section
    "about.title": "The Company",
    "about.description1": "LIFTCO E.E. technical elevator company, located in Thessaloniki, has been active in the elevator sector since 1935 with very high productivity levels in three main areas: installation, repair and maintenance of elevators, escalators and all types of lifting equipment for people and goods.",
    "about.description2": "With high turnover and 10 employees, it holds one of the best positions in the field of elevators and escalators in Thessaloniki and Northern Greece.",
    "about.cert": "DIN ISO 9001/1992 Certification",
    "about.staff": "Specialized personnel",
    "about.equipment": "Modern equipment",
    "about.prices": "Competitive prices",
    "about.more": "Learn More",
    
    // CTA Section
    "cta.title": "Need an Elevator?",
    "cta.description": "Contact us to discuss your needs and find the ideal solutions together. We serve Thessaloniki and all of Northern Greece.",
    
    // Stats
    "stats.title": "LIFTCO in",
    "stats.titleHighlight": "Numbers",
    "stats.subtitle": "Decades of experience and thousands of successful projects make us your reliable partner.",
    "stats.years": "Years of Experience",
    "stats.installations": "Installations",
    "stats.clients": "Satisfied Clients",
    "stats.support": "Support",
    
    // Testimonials
    "testimonials.title": "What Our Clients Say",
    "testimonials.subtitle": "The trust of thousands of customers gives us the strength to continue with the same passion.",
    
    // Google Reviews
    "reviews.title": "Reviews",
    "reviews.count": "reviews on Google",
    "reviews.reviewFrom": "Review from",
    "reviews.seeAll": "See all reviews on Google Maps",
    
    // Footer
    "footer.description": "Elevator & Escalator Technical Company in Thessaloniki since 1935.",
    "footer.quicklinks": "Quick Links",
    "footer.services": "Services",
    "footer.contact": "Contact",
    "footer.rights": "All rights reserved.",
    "footer.serving": "Serving Thessaloniki & Northern Greece",
    "footer.24h": "24/7 Service",

    // Common
    "common.more": "Learn more",
    "common.allServices": "All Services",
    "common.contactForm": "Contact",
    "common.needHelp": "Need Help?",
    "common.callNow": "Call Us Now",

    // Company
    "company.address": "Amyndas 18, Thessaloniki",

    // Not Found
    "notfound.title": "Page not found",
    "notfound.subtitle": "The page you are looking for doesn't exist.",
    "notfound.home": "Home",

    // Service Detail
    "serviceDetail.includes": "What's Included",
    "serviceDetail.requestQuote": "Request a Quote",
    "serviceDetail.requestQuoteText": "Contact us for a free assessment and quote with no obligation.",

    // Loading
    "loading": "Loading",

    // Cookie Consent
    "cookie.title": "🍪 Cookie Usage",
    "cookie.message": "We use cookies to improve your experience on our website. By continuing to browse, you agree to their use.",
    "cookie.accept": "Accept",
    "cookie.reject": "Decline",
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>("el");

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
